// 유도 줄기(c017 장비 연출, 3차 모듈): 바닥 드롭이 영웅에게 빨려 드는 나선 줄기와, 줍는 순간 빛기둥이 아래로 접히는 기둥.
// 한 슬롯 = 카메라를 향해 폭을 펼친 띠(리본) 한 장. 머리(u = 1)는 굵고 뜨겁고, 꼬리(u = 0)로 갈수록 가늘고 옅다. 드로우콜 1.
// variant 0 = 나선 유도 줄기: 시작점(x,y,z)에서 대상(follow로 붙인 FxTarget, 매 프레임 그 위치) 가슴 높이로 날아간다.
//   경로 = 직선 보간 + 위로 솟는 아치(roll m) + 경로 축을 감는 나선(반경 from m가 끝으로 갈수록 0, 감는 각 sweep rad, 시작 각 start rad).
//   머리는 처음엔 머뭇거리다 빨려 들듯 가속한다(진행 = (age/T)^1.7, T = 이동 시간 = life / (1 + tail)). 꼬리는 tail(이동 시간 비율)만큼 늦게 따라온다.
//   대상이 없으면 끝점 = 시작점 + yaw 방향 ahead m, 높이 + length m.
// variant 1 = 접히는 기둥: 바닥(y)에서 높이 length m의 세로 띠가 life 동안 위에서부터 아래로 접히며 가늘어진다. 머리(밝은 매듭) = 아래.
// 쓰는 칸: length(대상 위 높이 / 기둥 높이), width(머리 폭 m), from(나선 시작 반경), sweep(감는 각), start(시작 각), roll(아치 높이), tail, life, ahead, color(몸통)·color2(머리·심)·intensity·seed.
// 프레임 루프에서 객체를 만들지 않는다(경로 점은 모듈 공용 Float64Array에 쓴다).
import * as THREE from 'three/webgpu';
import { abs, clamp, mix, smoothstep, texture, uv, vec2 } from 'three/tsl';
import { BatchModule, fxAttributes, type Slot } from './batch';
import { stripLayout } from './layout';
import { fxMaterial } from './material';
import { clamp01 } from './path';
import { MODULE_CAPS, type FxTarget, type ModuleKind } from './types';

export const STREAK_SEG = 18;
/** 머리 진행 곡선 지수(빨려 드는 가속) */
export const STREAK_PULL = 1.7;
/** 기본 시선 방향(전투 카메라). 렌더가 setView로 매 프레임 바꾼다 */
const VIEW0 = [0, -0.64, -0.77] as const;
/** 경로 점 (SEG+1)×3 — 모듈 공용 임시 버퍼(할당 없음) */
const P = new Float64Array((STREAK_SEG + 1) * 3);

function makeMaterial(noise: THREE.Texture): THREE.MeshBasicNodeMaterial {
  const { ctl, t1, t2 } = fxAttributes();
  const age = ctl.x, env = ctl.y, seed = ctl.z;
  const u = uv().x, v = uv().y;
  const band = abs(v.mul(2).sub(1));
  const across = band.oneMinus().pow(1.1);
  const core = band.oneMinus().pow(4);
  // 꼬리(0) → 머리(1)
  const along = u.pow(1.1);
  const head = smoothstep(0.72, 1.0, u);
  const flow = texture(noise, vec2(u.mul(3.2).sub(age.mul(t2.w)), v.mul(0.5).add(seed))).g;
  const alpha = clamp(along.mul(across).mul(flow.mul(0.45).add(0.8)).add(head.mul(core).mul(0.9)), 0, 1).mul(env);
  const hot = clamp(head.mul(0.75).add(core.mul(0.6)), 0, 1);
  const col = mix(t1.rgb, t2.rgb, hot).mul(t1.w);
  return fxMaterial(col, alpha);
}

export class StreakModule extends BatchModule {
  readonly kind: ModuleKind = 'streak';
  private nextTarget: FxTarget | null = null;
  private vx: number = VIEW0[0]; private vy: number = VIEW0[1]; private vz: number = VIEW0[2];

  constructor(noise: THREE.Texture, cap = MODULE_CAPS.streak) {
    super(cap, stripLayout(STREAK_SEG), makeMaterial(noise), 23, 'fx-streak');
  }

  /** 카메라 시선 방향(월드). 길이가 0이면 무시한다 */
  setView(x: number, y: number, z: number): void {
    const l = Math.hypot(x, y, z);
    if (l < 1e-6) return;
    this.vx = x / l; this.vy = y / l; this.vz = z / l;
  }

  /** 이 호출 뒤에 spawn하는 줄기가 target으로 날아간다(null이면 고정 끝점) */
  follow(target: FxTarget | null): void { this.nextTarget = target; }

  protected override onSpawn(s: Slot): void { s.tgt = this.nextTarget; }

  /** 이동 시간(초): 머리가 끝점에 닿는 시각 */
  static travel(life: number, tail: number): number { return life / (1 + Math.max(0, tail)); }

  protected positions(s: Slot, out: Float32Array, o: number): void {
    if (s.variant >= 0.5) this.column(s, out, o);
    else this.spiral(s, out, o);
  }

  /** 접히는 기둥: 위(꼬리 u=0) → 아래(머리 u=1) 세로 띠, 폭 = 수평 시선의 옆 방향 */
  private column(s: Slot, out: Float32Array, o: number): void {
    const a = clamp01(s.age / s.life);
    const H = s.length * s.scale * (1 - a * a * (3 - 2 * a)) + 0.05;
    const W = s.width * s.scale * (1 - 0.55 * a);
    let wx = this.vz, wz = -this.vx;
    const wl = Math.hypot(wx, wz);
    if (wl < 1e-3) { wx = 1; wz = 0; } else { wx /= wl; wz /= wl; }
    for (let k = 0; k <= STREAK_SEG; k++) {
      const f = k / STREAK_SEG;
      const y = s.y + H * (1 - f);
      // 위쪽은 가늘게, 매듭(아래) 쪽은 굵게
      const hw = 0.5 * W * (0.35 + 0.65 * f);
      const q = o + k * 6;
      out[q] = s.x - wx * hw; out[q + 1] = y; out[q + 2] = s.z - wz * hw;
      out[q + 3] = s.x + wx * hw; out[q + 4] = y; out[q + 5] = s.z + wz * hw;
    }
  }

  private spiral(s: Slot, out: Float32Array, o: number): void {
    const t = s.tgt;
    const ex = t !== null ? t.x : s.x + Math.sin(s.yaw) * s.ahead;
    const ey = (t !== null ? t.y : s.y) + s.length;
    const ez = t !== null ? t.z : s.z + Math.cos(s.yaw) * s.ahead;
    const sx = s.x, sy = s.y, sz = s.z;
    let dx = ex - sx, dy = ey - sy, dz = ez - sz;
    const dl = Math.hypot(dx, dy, dz);
    if (dl > 1e-4) { dx /= dl; dy /= dl; dz /= dl; } else { dx = 0; dy = 1; dz = 0; }
    // 나선 기저: n1 = 경로 × 위(수평), n2 = n1 × 경로
    let n1x = dz, n1z = -dx;
    const nl = Math.hypot(n1x, n1z);
    if (nl < 1e-3) { n1x = 1; n1z = 0; } else { n1x /= nl; n1z /= nl; }
    const n2x = -dy * n1z, n2y = n1z * dx - n1x * dz, n2z = dy * n1x;
    const tail = Math.max(0.02, s.tail);
    const T = StreakModule.travel(s.life, tail);
    const a = s.age / T;
    const R0 = s.from * s.scale;
    for (let k = 0; k <= STREAK_SEG; k++) {
      const f = k / STREAK_SEG;
      const ak = clamp01(a - tail * (1 - f));
      const p = Math.pow(ak, STREAK_PULL);
      const r = R0 * Math.pow(1 - p, 1.3);
      const th = s.start + s.sweep * p;
      const c = Math.cos(th) * r, sn = Math.sin(th) * r;
      const arch = s.roll * Math.sin(Math.PI * p);
      const q = k * 3;
      P[q] = sx + (ex - sx) * p + n1x * c + n2x * sn;
      P[q + 1] = sy + (ey - sy) * p + arch + n2y * sn;
      P[q + 2] = sz + (ez - sz) * p + n1z * c + n2z * sn;
    }
    const W = s.width * s.scale;
    for (let k = 0; k <= STREAK_SEG; k++) {
      // 접선: 이웃 점 차분(끝은 한쪽)
      const k0 = k > 0 ? k - 1 : k, k1 = k < STREAK_SEG ? k + 1 : k;
      let tx = P[k1 * 3] - P[k0 * 3], ty = P[k1 * 3 + 1] - P[k0 * 3 + 1], tz = P[k1 * 3 + 2] - P[k0 * 3 + 2];
      const tl = Math.hypot(tx, ty, tz);
      if (tl < 1e-5) { tx = dx; ty = dy; tz = dz; } else { tx /= tl; ty /= tl; tz /= tl; }
      // 폭 = 접선 × 시선(카메라를 향해 펼친다). 거의 나란하면 나선 기저 n1
      let wx = ty * this.vz - tz * this.vy, wy = tz * this.vx - tx * this.vz, wz = tx * this.vy - ty * this.vx;
      const wl = Math.hypot(wx, wy, wz);
      if (wl < 0.1) { wx = n1x; wy = 0; wz = n1z; } else { wx /= wl; wy /= wl; wz /= wl; }
      const hw = 0.5 * W * Math.pow(k / STREAK_SEG, 0.8);
      const px = P[k * 3], py = P[k * 3 + 1], pz = P[k * 3 + 2];
      const q = o + k * 6;
      out[q] = px - wx * hw; out[q + 1] = py - wy * hw; out[q + 2] = pz - wz * hw;
      out[q + 3] = px + wx * hw; out[q + 4] = py + wy * hw; out[q + 5] = pz + wz * hw;
    }
  }

  /** 머리 위치(QA·시험): 대상 기준 지금 머리가 있는 점을 out에 쓴다 */
  headOf(i: number, out: { x: number; y: number; z: number }): boolean {
    if (i < 0 || i >= this.n) return false;
    const s = this.slots[i];
    const o = (i * (STREAK_SEG + 1) * 2 + STREAK_SEG * 2) * 3;
    out.x = (this.pos[o] + this.pos[o + 3]) / 2; out.y = (this.pos[o + 1] + this.pos[o + 4]) / 2; out.z = (this.pos[o + 2] + this.pos[o + 5]) / 2;
    return s.variant < 0.5;
  }

  protected ctl(s: Slot, out: Float32Array): void {
    const age01 = clamp01(s.age / s.life);
    out[0] = age01;
    // 짧게 켜지고 마지막 18%에서 꺼진다(기둥은 접히는 동안 내내 밝다가 끝에서)
    const fade = s.variant >= 0.5 ? (age01 - 0.75) / 0.25 : (age01 - 0.82) / 0.18;
    out[1] = Math.min(1, s.age / 0.025) * (1 - clamp01(fade));
    out[2] = s.seed;
    out[3] = 0;
  }

  protected override tint2w(s: Slot): number { return s.variant >= 0.5 ? 1.6 : 2.6; }
}
